import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { checkPatientEligibility } from '../../services/api';
import type { EligibilityCheckResponse } from '../../services/api';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { useToast } from '../../hooks/useToastContext';

interface RunEligibilityCheckButtonProps {
  patientId: string;
  patientInsuranceId: string;
  /** Why the check cannot run. When set, the button is disabled and explains itself. */
  blockedReason?: string;
  label?: string;
  size?: 'sm' | 'default';
  onChecked?: (result: EligibilityCheckResponse) => void;
}

/**
 * Runs a 270 for one specific coverage and reports the result.
 *
 * The coverage is always named by the caller. The chart, an episode, and billing
 * each have a different idea of the patient's current payer, so this component
 * never picks one.
 */
export const RunEligibilityCheckButton: React.FC<
  RunEligibilityCheckButtonProps
> = ({
  patientId,
  patientInsuranceId,
  blockedReason,
  label = 'Run eligibility check',
  size = 'sm',
  onChecked,
}) => {
  const { error: showError, success: showSuccess } = useToast();
  const [isRunning, setIsRunning] = useState(false);

  const runCheck = async () => {
    setIsRunning(true);
    try {
      const res = await checkPatientEligibility({
        path: { patient_id: patientId },
        body: { patient_insurance_id: patientInsuranceId },
      });
      if (res.error) {
        console.error('Eligibility check failed:', res.error);
        showError(
          getBusinessErrorMessage(res) ??
            'Unable to run the eligibility check. Please try again.'
        );
        return;
      }
      if (res.data) {
        showSuccess('Eligibility check complete');
        onChecked?.(res.data);
      }
    } catch (err) {
      console.error('Eligibility check failed:', err);
      showError('Unable to run the eligibility check. Please try again.');
    } finally {
      setIsRunning(false);
    }
  };

  const button = (
    <Button
      size={size}
      variant='outline'
      disabled={isRunning || Boolean(blockedReason)}
      onClick={() => void runCheck()}
    >
      <RefreshCw
        className={`h-3.5 w-3.5 mr-1.5 ${isRunning ? 'animate-spin' : ''}`}
      />
      {isRunning ? 'Checking…' : label}
    </Button>
  );

  if (!blockedReason) return button;

  // A disabled button with no reason is the thing clinicians file tickets about.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex'>{button}</span>
        </TooltipTrigger>
        <TooltipContent>{blockedReason}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
